import GameAnalytics from "not-game-analytics"

import App from "@frog/source/models/App.js"
import Stage from "@frog/source/models/Stage.js"
import Step from "@frog/source/models/Step.js"
import Geometry from "@frog/source/utility/Geometry.js"
import Elements from "@frog/source/data/elements.js"

import emberUnitJobs from "@frog/source/data/games/ember/unitJobs.js"

import * as PlotFlourishes from "@frog/source/data/plot/flourishes.js"

function canTarget({performerKey, stage, position}) {
    const performer = stage.units[performerKey]
    const target = Stage.findUnitByPosition(stage, position)
    if(target == undefined) return false
    if(target.key == performer.key) return false
    if(target.affiliation == undefined) return false
    if(target.affiliation == "none") return false
    if(target.affiliation == "nothing") return false // TODO: should this be a check for type == "thing"??
    return true
}

function getSupportPriority({performerKey, stage, position}) {
    const performer = stage.units[performerKey]
    const target = Stage.findUnitByPosition(stage, position)
    if(target != undefined
    && target.affiliation == performer.affiliation) {
        return 5
    } else {
        return 4
    }
}

const skills = {}

skills["inflict-damage"] = {
    "key": "inflict-damage",
    "name": "Attack",
    "role": "damage",

    "type": "invoke",

    "canBeCounterattacked": true,
    "canCounterattack": ({performerKey, stage, position}) => {
        const performer = stage.units[performerKey]
        if(performer?.ai?.isNotCounterattacking == true) return false
        return true
    },

    "isRenderedEvenIfCanNotTarget": true,

    "power": 0, // will default to the player's power.
    "isIgnoringArmor": false,
    "isIgnoringElements": false,

    // "isHidden": true,

    "canTarget": function({performerKey, stage, position}) {
        const performer = stage.units[performerKey]
        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return false
        if(performer.ai?.behaviors?.includes("can attack plants") && target.role == "plant") return true
        if(performer.ai?.behaviors?.includes("can attack flames") && target.role == "flame") return true
        if(target.affiliation == "none") return false
        if(target.affiliation == "nothing") return false
        // if(target.affiliation == undefined) return false
        if(target.type == "thing") return false
        if(performer.canBetray != true && target.affiliation == performer.affiliation) return false
        if(performer.ai?.behaviors?.includes("never attacks player") && target.affiliation == stage.player.affiliation) return false
        if(performer.energy["invoke skill"] != true) return false // you can cancel an attack by removing their energy
        return true
    },
    "getPriority": function({performerKey, stage, position}) {
        const performer = stage.units[performerKey]
        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return 0
        if(target.affiliation != performer.affiliation) return 5
        if(target.properties?.includes("can be pacified")) return 5
        return 4
    },

    "calculateStep": function({stage, position, performerKey, effectKey}) {
        const steps = []

        let performer = stage.units[performerKey]
        let target = Stage.findUnitByPosition(stage, position)

        const direction = Geometry.direction(position, performer.position)
        // const distance = Geometry.distance(position, performer.position)

        // Pre-Attack Dialogue //
        if(performer.scripts != undefined
        && performer.scripts["attack"] != undefined) {
            steps.push(...Stage.processEventSteps(stage, performer.scripts["attack"]))

            if(steps[steps.length - 1] != undefined
            && steps[steps.length - 1].omegastage != undefined) {
                stage = steps[steps.length - 1].omegastage
            }
        }
        stage.scripts.forEach((condition) => {
            if(condition.trigger == "before attacking"
            && condition.canBeTriggered(stage, performer, target) == true
            && condition.events != undefined) {

                steps.push(...Stage.processEventSteps(stage, condition.events))

                if(steps[steps.length - 1] != undefined
                && steps[steps.length - 1].omegastage != undefined) {
                    stage = steps[steps.length - 1].omegastage
                }
            }
        })

        // if(target == undefined) return
        // const previousAffiliation = target.affiliation
        // target = Stage.findUnitByPosition(stage, position)
        // const newAffiliation = target.affiliation
        //
        // if(previousAffiliation != stage.player.affiliation
        // && newAffiliation == stage.player.affiliation) {
        //     // if we converted them from this scripts, cancel the attack!
        //     // someday, add a more verbose way to purposefully cancel an attack from a before attacking script
        //     return steps
        // }

        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })
        steps.push(step)

        // TODO: Unclear why we are re-checking canTarget - isn't that
        // checked long before the calculateStep is ever called??
        // oh, i figured it out - it is for when a before-attacking script changes the state to allow the character to flip
        if(this.canTarget({performerKey, stage, position}) != true) return steps

        performer = step.omegastage.units[performerKey]
        target = Stage.findUnitByPosition(step.omegastage, position)
        if(target == undefined) return steps

        /////////////
        // DAMAGE //
        ///////////

        // const range = (this.range && this.range.max) || (performer.equippedItem?.attackrange && performer.equippedItem?.attackrange?.max) || (performer.attackrange && performer.attackrange.max) || 1
        let hitmark = 200 // + ((distance - 1) * 50)

        let innatePower = (performer.power || 0) + (performer.equippedItem?.power || 0)
        let damage = innatePower

        if(performer.properties?.includes("counter") || performer.equippedItem?.properties?.includes("counter")) {
            if(performer.affiliation == stage.player.affiliation && stage.turn.number % 2 != 0
            || performer.affiliation != stage.player.affiliation && stage.turn.number % 2 == 0) {
                damage *= 2
            }
        }

        if(performer.properties?.includes("engage") || performer.equippedItem?.properties?.includes("engage")) {
            if(target.health == target.maxhealth) {
                damage *= 2
            }
        }

        if(performer.equippedItem?.powerboost instanceof Function) {
            damage += performer.equippedItem.powerboost({"stage": stage, "unit": performer, "item": performer.equippedItem}) ?? 0
        }

        if(target.effects?.wounded != undefined) {
            damage += target.effects.wounded.power
        }

        const attacktype = performer.equippedItem?.attacktype || performer.attacktype
        if(attacktype == "steel") damage -= target.defense || 0
        if(attacktype == "magic") damage -= target.resistance || 0

        damage = Math.max(0, damage)

        const attackElement = performer.equippedItem?.element || performer.element || performer.attackelement
        const defendElement = target.equippedItem?.element || target.element || target.attack?.element
        let isSuperEffective = false
        let isIneffective = false
        if(performer.properties?.includes("always attacks twice")) {
            isSuperEffective = true
        }
        if(attackElement != undefined) {
            if(Elements[defendElement]?.weaknesses != undefined
            && Elements[defendElement].weaknesses.includes(attackElement)) {
                // We don't need to double the damage or anything, since we'll
                // instead be attacking twice!! Although the second attack
                // might be slightly weaker, given the balance of the game.
                isSuperEffective = true
            }
            if(Elements[defendElement]?.strengths != undefined
            && Elements[defendElement].strengths.includes(attackElement)) {
                // Decrease the damage by a half...
                let damageReduction = damage / 2

                // ...rounded to the nearest 5...
                damageReduction = Math.floor(damageReduction / 5) * 5

                // ...with a minimum of 5.
                damageReduction = Math.max(5, damageReduction)

                damage -= damageReduction
                isIneffective = true
            }
        }

        ////////////////
        // ANIMATION //
        //////////////

        Step.add(step, PlotFlourishes.SwingBackMovement({
            "mark": 0,
            "unitKey": performer.key,
            "position": performer.position,
            "direction": direction,
        }))
        Step.add(step, {
            "mark": 180,
            "audio": {"triggerKey": performer.attackaudio ?? "Unit:Attack:Melee"}
        })

        if(target.properties?.includes("dodging") || target.equippedItem?.properties?.includes("dodging")) {
            Step.add(step, PlotFlourishes.NudgedMovement({
                "mark": hitmark / 2,
                "unitKey": target.key,
                "position": target.position,
                "direction": Geometry.multiply(Geometry.perpendicular(direction), 1.5),
            }))
            Step.add(step, PlotFlourishes.TextFloat({
                "mark": hitmark,
                "text": "DODGED",
                "unitKey": target.key,
                "position": {
                    "x": target.position.x,
                    "y": target.position.y + 0.3,
                },
            }))
            Step.add(step, {
                "mark": 180,
                "unit": {
                    "key": target.key,
                    "health": target.health - 0.1
                }
            })
            return steps
        }
        if(performer.properties?.includes("weaken") || performer.equippedItem?.properties?.includes("weaken")) {
            const power = (performer.power || 0) + (performer.equippedItem?.power || 0)
            Step.add(step, {
                "mark": 180,
                "unit": {
                    "key": target.key,
                    "power": (target.power || 0) - power
                }
            })
        }
        if(performer.properties?.includes("poisonous") || performer.equippedItem?.properties?.includes("poisonous")) {
            const power = (performer.power || 0) + (performer.equippedItem?.power || 0)
            const existingEffect = target.effects?.poisoned
            Step.add(step, {
                "mark": 180,
                "unit": {
                    "key": target.key,
                    "effects": {
                        "poisoned": {
                            "key": "poisoned",
                            "power": (existingEffect?.power || 0) + power
                        }
                    }
                }
            })
        }
        if(performer.properties?.includes("wounding") || performer.equippedItem?.properties?.includes("wounding")) {
            const power = (performer.power || 0) + (performer.equippedItem?.power || 0)
            const existingEffect = target.effects?.wounded
            Step.add(step, {
                "mark": 180,
                "unit": {
                    "key": target.key,
                    "effects": {
                        "wounded": {
                            "key": "wounded",
                            "power": (existingEffect?.power || 0) + power
                        }
                    }
                }
            })
        }

        if(damage == 0) {
            Step.add(step, {
                "mark": hitmark,
                "audio": {"triggerKey": "Unit:Attack:NoDamage"}
            })
        } else if(damage > 0) {
            Step.add(step, {
                "mark": hitmark,
                "unit": {
                    "key": target.key,
                    "health": Math.min(target.maxhealth, Math.max(0, target.health - damage)),
                },
            })

            if(performer.properties?.includes("pain") || performer.equippedItem?.properties?.includes("pain")) {
                const power = (performer.power || 0) + (performer.equippedItem?.power || 0)
                Step.add(step, {
                    "mark": hitmark,
                    "unit": {
                        "key": performer.key,
                        "health": performer.health - damage
                    }
                })
                let printedDamage = damage / 10 // DICE! divided by 10
                if(printedDamage == 0.5) printedDamage = "½"
                printedDamage = "-" + printedDamage + "hp"
                Step.add(step, PlotFlourishes.TextFloat({
                    "mark": hitmark,
                    "type": "damage",
                    "unitKey": performer.key,
                    "position": performer.position,
                    "text": printedDamage
                }))
            }

            if(isSuperEffective != true
            && performer?.properties?.includes("looting")) {
                if(target.health <= 0) {
                    // TODO: Add modal to inform about the new weapon
                    Step.add(step, {
                        "mark": hitmark,
                        "unit": {
                            "key": performer.key,
                            "element": defendElement,
                            "weaponImageKey": target.weaponImageKey,
                            "attacktype": target.attacktype,
                            "attackrange": target.attackrange,
                            "power": target.power,
                        }
                    })
                    Step.add(step, {
                        "mark": hitmark,
                        "unit": {
                            "key": target.key,
                            "element": "undefined",
                            "weaponImageKey": "",
                            "attacktype": "none",
                            "attackrange": 1,
                            "power": 10,
                        }
                    })
                }
            }

            if(isSuperEffective != true
            && (performer.properties?.includes("forceful") || performer.equippedItem?.properties?.includes("forceful"))) {
                const direction = Geometry.direction(position, performer.position)
                const vector = Geometry.vector(position, performer.position)

                const previousPosition = position
                const nextPosition = Geometry.add(position, vector)

                const collision = Stage.findCollision({
                    stage, previousPosition, nextPosition,
                })

                if(collision == undefined) {
                    Step.add(step, PlotFlourishes.PushedMovement({
                        "mark": hitmark,
                        "unitKey": target.key,
                        "startPosition": previousPosition,
                        "endPosition": nextPosition,
                    }))
                    // Step.add(step, {
                    //     "mark": hitmark,
                    //     "unit": {
                    //         "key": target.key,
                    //         "position": nextPosition,
                    //     }
                    // })
                } else if(collision != undefined) {
                    Step.add(step, PlotFlourishes.NudgedMovement({
                        "mark": hitmark,
                        "unitKey": target.key,
                        "position": previousPosition,
                        "direction": direction,
                    }))

                    const damage = 10
                    Step.add(step, {
                        "mark": hitmark,
                        "unit": {
                            "key": target.key,
                            "health": target.health - damage
                        },
                    })

                    if(collision.unit != undefined) {
                        Step.add(step, {
                            "mark": hitmark,
                            "unit": {
                                "key": collision.unit.key,
                                "health": collision.unit.health - damage
                            },
                        })
                    }
                }
            } else if(isSuperEffective != true && performer.properties?.includes("leering")) {
                const direction = Geometry.direction(position, performer.position)
                const oppositeDirection = Geometry.flip(direction)

                const previousPosition = performer.position
                const nextPosition = Geometry.add(performer.position, oppositeDirection)

                const previousTargetPosition = target.position
                const nextTargetPosition = Geometry.add(target.position, oppositeDirection)

                const collision = Stage.findCollision({
                    "stage": stage,
                    previousPosition,
                    nextPosition,
                })

                if(collision == undefined) {
                    Step.add(step, PlotFlourishes.PushedMovement({
                        "mark": hitmark,
                        "unitKey": performer.key,
                        "startPosition": previousPosition,
                        "endPosition": nextPosition,
                    }))
                    Step.add(step, PlotFlourishes.PushedMovement({
                        "mark": hitmark,
                        "unitKey": target.key,
                        "startPosition": previousTargetPosition,
                        "endPosition": nextTargetPosition,
                    }))
                    // Step.add(step, {"mark": 0, "audio": {"triggerKey": "Unit:Pull"}})
                } else if(collision != undefined) {
                    Step.add(step, PlotFlourishes.NudgedMovement({
                        "mark": hitmark,
                        "unitKey": performer.key,
                        "position": performer.position,
                        "direction": direction,
                    }))
                    Step.add(step, PlotFlourishes.NudgedMovement({
                        "mark": hitmark,
                        "unitKey": target.key,
                        "position": target.position,
                        "direction": direction,
                    }))
                    // Step.add(step, {"mark": 0, "audio": {"triggerKey": "Unit:Attack:Utility"}})
                }
            } else {
                Step.add(step, PlotFlourishes.NudgedMovement({
                    "mark": hitmark,
                    "unitKey": target.key,
                    "position": position,
                    "direction": direction,
                }))
            }

            Step.add(step, {
                "mark": hitmark,
                "audio": {"triggerKey": target.health < 0 ? "Unit:Attack:FinalHit" : "Unit:Attack:Hit"}
            })
            Step.add(step, {"mark": hitmark, "audio": {"triggerKey": performer.equippedItem?.weaponAudioKey || performer.weaponAudioKey}})

            if(performer.properties != undefined
            && performer.properties.includes("lifesteal")) {
                Step.add(step, {
                    "mark": hitmark,
                    "unit": {
                        "key": performer.key,
                        "health": Math.min(performer.maxhealth, Math.max(0, performer.health + 5)),
                    },
                })
                Step.add(step, PlotFlourishes.TextFloat({
                    "mark": hitmark,
                    "type": "healing",
                    "unitKey": performer.key,
                    "position": performer.position,
                    "text": "+5",
                }))
            }
        }
        let printedDamage = damage / 10 // DICE! divided by 10
        if(printedDamage == 0.5) printedDamage = "½"
        printedDamage = "-" + printedDamage + "hp"
        Step.add(step, PlotFlourishes.TextFloat({
            "mark": hitmark,
            "type": "damage",
            "unitKey": target.key,
            "position": target.position,
            "text": printedDamage
        }))

        if(isSuperEffective) {
            // damage -= Math.ceil(damage * (1 / 2)) // DICE
            hitmark *= 2
            hitmark += 50 // to add a short pause between the attacks

            Step.add(step, PlotFlourishes.SwingBackMovement({
                "mark": hitmark - 133,
                "unitKey": performer.key,
                "position": performer.position,
                "direction": direction,
            }))
            Step.add(step, {
                "mark": hitmark + 180,
                "audio": {"triggerKey": performer.attackaudio ?? "Unit:Attack:Melee"}
            })
            Step.add(step, {
                "mark": hitmark + 180,
                "audio": {"triggerKey": "Unit:Attack:" + (performer.equippedItem?.element || performer.element)}
            })

            if(damage == 0) {
                Step.add(step, {
                    "mark": hitmark,
                    "audio": {"triggerKey": "Unit:Attack:NoDamage"}
                })
            }
            if(damage > 0) {
                Step.add(step, {
                    "mark": hitmark,
                    "unit": {
                        "key": target.key,
                        "health": Math.min(target.maxhealth, Math.max(0, target.health - damage)),
                    },
                })

                if(performer.properties?.includes("pain") || performer.equippedItem?.properties?.includes("pain")) {
                    const power = (performer.power || 0) + (performer.equippedItem?.power || 0)
                    Step.add(step, {
                        "mark": hitmark,
                        "unit": {
                            "key": performer.key,
                            "health": performer.health - damage
                        }
                    })
                    let printedDamage = damage / 10 // DICE! divided by 10
                    if(printedDamage == 0.5) printedDamage = "½"
                    printedDamage = "-" + printedDamage + "hp"
                    Step.add(step, PlotFlourishes.TextFloat({
                        "mark": hitmark,
                        "type": "damage",
                        "unitKey": performer.key,
                        "position": performer.position,
                        "text": printedDamage
                    }))
                }

                if(performer?.properties?.includes("looting")) {
                    if(target.health <= 0) {
                        // TODO: Add modal to inform about the new weapon
                        Step.add(step, {
                            "mark": hitmark,
                            "unit": {
                                "key": performer.key,
                                "element": defendElement,
                                "weaponImageKey": target.weaponImageKey,
                                "attacktype": target.attacktype,
                                "attackrange": target.attackrange,
                                "power": target.power,
                            }
                        })
                        Step.add(step, {
                            "mark": hitmark,
                            "unit": {
                                "key": target.key,
                                "element": "undefined",
                                "weaponImageKey": "none",
                                "attacktype": "none",
                                "attackrange": 1,
                                "power": 10,
                            }
                        })
                    }
                }

                if(performer.properties?.includes("forceful") || performer.equippedItem?.properties?.includes("forceful")) {
                    const direction = Geometry.direction(position, performer.position)
                    const vector = Geometry.vector(position, performer.position)

                    const previousPosition = position
                    const nextPosition = Geometry.add(position, vector)

                    const collision = Stage.findCollision({
                        stage, previousPosition, nextPosition,
                    })

                    if(collision == undefined) {
                        Step.add(step, PlotFlourishes.PushedMovement({
                            "mark": hitmark,
                            "unitKey": target.key,
                            "startPosition": previousPosition,
                            "endPosition": nextPosition,
                        }))
                        // Step.add(step, {
                        //     "mark": hitmark,
                        //     "unit": {
                        //         "key": target.key,
                        //         "position": nextPosition,
                        //     }
                        // })
                    } else if(collision != undefined) {
                        Step.add(step, PlotFlourishes.NudgedMovement({
                            "mark": hitmark,
                            "unitKey": target.key,
                            "position": target.position,
                            "direction": direction,
                        }))

                        const damage = 10
                        Step.add(step, {
                            "mark": hitmark,
                            "unit": {
                                "key": target.key,
                                "health": target.health - damage
                            },
                        })

                        if(collision.unit != undefined) {
                            Step.add(step, {
                                "mark": hitmark,
                                "unit": {
                                    "key": collision.unit.key,
                                    "health": collision.unit.health - damage
                                },
                            })
                        }
                    }
                } else if(performer.properties?.includes("leering")) {
                    const direction = Geometry.direction(position, performer.position)
                    const oppositeDirection = Geometry.flip(direction)

                    const previousPosition = performer.position
                    const nextPosition = Geometry.add(performer.position, oppositeDirection)

                    const previousTargetPosition = target.position
                    const nextTargetPosition = Geometry.add(target.position, oppositeDirection)

                    const collision = Stage.findCollision({
                        "stage": stage,
                        previousPosition,
                        nextPosition,
                    })

                    if(collision == undefined) {
                        Step.add(step, PlotFlourishes.PushedMovement({
                            "mark": hitmark,
                            "unitKey": performer.key,
                            "startPosition": previousPosition,
                            "endPosition": nextPosition,
                        }))
                        Step.add(step, PlotFlourishes.PushedMovement({
                            "mark": hitmark,
                            "unitKey": target.key,
                            "startPosition": previousTargetPosition,
                            "endPosition": nextTargetPosition,
                        }))
                        // Step.add(step, {"mark": 0, "audio": {"triggerKey": "Unit:Pull"}})
                    } else if(collision != undefined) {
                        Step.add(step, PlotFlourishes.NudgedMovement({
                            "mark": hitmark,
                            "unitKey": performer.key,
                            "position": performer.position,
                            "direction": direction,
                        }))
                        Step.add(step, PlotFlourishes.NudgedMovement({
                            "mark": hitmark,
                            "unitKey": target.key,
                            "position": target.position,
                            "direction": direction,
                        }))
                        // Step.add(step, {"mark": 0, "audio": {"triggerKey": "Unit:Attack:Utility"}})
                    }
                } else {
                    Step.add(step, PlotFlourishes.NudgedMovement({
                        "mark": hitmark,
                        "unitKey": target.key,
                        "position": target.position,
                        "direction": direction,
                    }))
                }

                // Step.add(step, PlotFlourishes.NudgedMovement({
                //     "mark": hitmark,
                //     "unitKey": target.key,
                //     "position": target.position,
                //     "direction": direction,
                // }))
                Step.add(step, {
                    "mark": hitmark,
                    "audio": {"triggerKey": target.health < 0 ? "Unit:Attack:FinalHit" : "Unit:Attack:Hit"}
                })
                Step.add(step, {
                    "mark": hitmark,
                    "audio": {"triggerKey": performer.equippedItem?.weaponAudioKey || performer.weaponAudioKey}
                })

                if(performer.properties != undefined
                && performer.properties.includes("lifesteal")) {
                    Step.add(step, {
                        "mark": hitmark,
                        "unit": {
                            "key": performer.key,
                            "health": Math.min(performer.maxhealth, Math.max(0, performer.health + 10)),
                        },
                    })
                    Step.add(step, PlotFlourishes.TextFloat({
                        "mark": hitmark,
                        "type": "healing",
                        "unitKey": performer.key,
                        "position": performer.position,
                        "text": "+5",
                    }))
                }
            }
            let printedDamage = damage / 10 // DICE! divided by 10
            if(printedDamage == 0.5) printedDamage = "½"
            printedDamage = "-" + printedDamage + "hp"
            Step.add(step, PlotFlourishes.TextFloat({
                "mark": hitmark,
                "type": "damage",
                "unitKey": target.key,
                "position": target.position,
                "text": printedDamage
            }))
            Step.add(step, PlotFlourishes.TextFloat({
                "mark": hitmark,
                // "type": "damage",
                "unitKey": target.key,
                "position": {
                    "x": target.position.x,
                    "y": target.position.y + 0.3,
                },
                "scale": 0.5,
                "rotate": 12,
                "text": "effective!!",
            })) // DICE removed the super effective label
            // Step.add(step, PlotFlourishes.TextFloat({
            //     "mark": hitmark,
            //     // "type": "damage",
            //     "unitKey": target.key,
            //     "position": {
            //         "x": target.position.x,
            //         "y": target.position.y + 0.6,
            //     },
            //     "text": "EFFECTIVE",
            // }))
        }

        if(isIneffective) {
            Step.add(step, PlotFlourishes.TextFloat({
                "mark": hitmark,
                // "type": "damage",
                "unitKey": target.key,
                "position": {
                    "x": target.position.x,
                    "y": target.position.y + 0.3,
                },
                "scale": 0.5,
                "text": "ineffective",
            }))
        }

        if(performer.properties?.includes("rampage") || performer.equippedItem?.properties?.includes("rampage")) {
            if(target.health == 0) {
                Step.add(step, {
                    "mark": hitmark,
                    "unit": {
                        "key": performer.key,
                        "effects": {
                            "rampaging": {
                                "key": "rampaging",
                            }
                        }
                    }
                })
            }
        }

        ////////////////////
        // STATE CHANGES //
        //////////////////

        Step.add(step, {
            "mark": hitmark,
            "unit": {
                "key": target.key,
                "state": "aggroed",
            }
        })
        Step.add(step, {
            "mark": 0,
            "unit": {
                "key": performer.key,
                "state": "aggroed",
            }
        })

        // const encounter = Object.values(stage.world.zones).find((zone) => {
        //     return Zone.isInBounds(zone, target.position)
        // })
        // Object.values(stage.units).forEach((unit) => {
        //     if(unit.affiliation == "bad"
        //     && Zone.isInBounds(encounter, unit.position)) {
        //         Step.add(step, {
        //             "mark": hitmark,
        //             "unit": {
        //                 "key": unit.key,
        //                 "state": "aggroed",
        //             }
        //         })
        //     }
        // })

        if(performer.key == "swashbuckler" && target.role == "barrel") {
            Step.add(step, {"mark": hitmark, ...Step.deltaflag("swashbuckler has attacked a barrel", true)})
        }

        if(performer.affiliation == stage.player.affiliation) {
            if(stage.flags["tutorial:hasAttacked"] != true) {
                Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:hasAttacked", true)})
            }
            if(isIneffective == true
            && stage.flags["tutorial:hasAttackedIneffectively"] != true) {
                Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:hasAttackedIneffectively", true)})
                Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:IneffectiveAttackerElement", attackElement)})
                Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:IneffectiveAttackedElement", defendElement)})
                Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:latestAttackEffectivity", "isIneffective")})
            }
            if(isSuperEffective == true
            && stage.flags["tutorial:hasAttackedSuperEffectively"] != true) {
                Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:hasAttackedSuperEffectively", true)})
                Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:SuperEffectiveAttackerElement", attackElement)})
                Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:SuperEffectiveAttackedElement", defendElement)})
                Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:latestAttackEffectivity", "isSuperEffective")})
            }
            if((attackElement == "earth" || attackElement == "axe" || attackElement == "gun")
            && (defendElement == "water" || defendElement == "spear" || defendElement == "bow")
            && stage.flags["tutorial:hasAttackedEffectivelyWithEarth"] != true) {
                Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:hasAttackedIneffectivelyWithEarth", true)})
            }
            if(target.attackrange != performer.attackrange
            && stage.flags["tutorial:hasAttackedWithoutCounterattack"] != true) {
                Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:hasAttackedWithoutCounterattack", true)})
            }
            if(Geometry.distance(performer.position, target.position) > 1
            && target.health > 0
            && stage.flags["tutorial:hasAttackedAtRange"] != true) {
                Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:hasAttackedAtRange", true)})
            }
            if(target.health == 0
            && stage.flags["tutorial:hasKilled"] != true) {
                Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:hasKilled", true)})
            }
            if((attackElement == "earth" || attackElement == "axe" || attackElement == "gun")
            && (defendElement == "earth" || defendElement == "axe" || defendElement == "gun")) {
                Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:hasAttackedBlueWithBlue", true)})
                Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:latestAttackEffectivity", "isNeutral")})
            }
            if((attackElement == "water" || attackElement == "spear" || attackElement == "bow")
            && (defendElement == "water" || defendElement == "spear" || defendElement == "bow")) {
                Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:hasAttackedGreenWithGreen", true)})
                Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:latestAttackEffectivity", "isNeutral")})
            }
            if((attackElement == "fire" || attackElement == "sword" || attackElement == "dagger")
            && (defendElement == "fire" || defendElement == "sword" || defendElement == "dagger")) {
                Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:hasAttackedRedWithRed", true)})
                Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:latestAttackEffectivity", "isNeutral")})
            }
            if((attackElement == "earth" || attackElement == "axe" || attackElement == "gun")) {
                Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:AttackerColor", "green")})
                Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:AttackerElement", "earth")})
            }
            if((attackElement == "water" || attackElement == "spear" || attackElement == "bow")) {
                Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:AttackerColor", "blue")})
                Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:AttackerElement", "water")})
            }
            if((attackElement == "fire" || attackElement == "sword" || attackElement == "dagger")) {
                Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:AttackerColor", "red")})
                Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:AttackerElement", "fire")})
            }
            if((defendElement == "earth" || defendElement == "axe" || defendElement == "gun")) {
                Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:AttackedColor", "green")})
                Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:AttackedElement", "earth")})
            }
            if((defendElement == "water" || defendElement == "spear" || defendElement == "bow")) {
                Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:AttackedColor", "blue")})
                Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:AttackedElement", "water")})
            }
            if((defendElement == "fire" || defendElement == "sword" || defendElement == "dagger")) {
                Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:AttackedColor", "red")})
                Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:AttackedElement", "fire")})
            }

        } else {
            Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:hasBeenAttacked", true)})
            if(stage.turn.number % 2 == 0) {
                Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:hasBeenAttackedInCounterattack", true)})
            }
        }

        // Post-Attack Dialogue //

        if(performer.canBetray == true
        && performer?.properties?.includes("no betrayals") != true) {
            if(performer.affiliation == stage.player.affiliation
            && target.affiliation == stage.player.affiliation) {
                Step.add(step, {
                    "mark": hitmark,
                    "unit": {
                        "key": target.key,
                        // "affiliation": target.affiliation != target.preferredAffiliation ? target.preferredAffiliation : stage.player.betrayedAffiliation,
                        "affiliation": target.betrayedAffiliation,
                        // "bodyKey": "neutral",
                    }
                })
                if(target.scripts != undefined
                && target.scripts["betrayed"] != undefined
                && target.health > 0) {
                    steps.push(...Stage.processEventSteps(step.omegastage, target.scripts["betrayed"]))
                }
            }
        }

        stage.scripts.forEach((condition) => {
            if(condition.trigger == "attacking"
            && condition.canBeTriggered(stage, performer, target) == true) {
                const conditionEvents = condition.events
                conditionEvents.unshift({
                    "omegastage": stage,
                    "name": "SavestateCapture",
                    "savestateSlug": condition.title || "Condition_" + stage.scripts.indexOf(condition),
                })
                steps.push(...Stage.processEventSteps(step.omegastage, conditionEvents))
            }
        })

        return steps
    }
}

skills["restore-health"] = {
    "key": "restore-health",
    "name": "Heal",
    "role": "support",

    "description": "Heal: Can restore health of an ally.",

    "type": "invoke",
    "range": null,

    // // "power": 15,
    // "power": 20, // DICE!! NOT SCALING WITH INNATE EQUIPMENT POWER!!!

    "getPriority": getSupportPriority,
    "canTarget": function({performerKey, stage, position}) {
        const performer = stage.units[performerKey]
        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return false
        if(target.type == "thing") return false
        if(target.key == performer.key) return false
        if(target.affiliation != performer.affiliation
        && target.properties?.includes("can be pacified") == false) {
            return false
        }
        return true
    },

    "calculateStep": function({performerKey, effectKey, stage, position}) {
        const steps = []

        const performer = stage.units[performerKey]
        const direction = Geometry.direction(position, performer.position)

        const hitmark = 0

        // if(target.maxhealth != target.health
        // && performer.scripts["help"] != undefined) {
        //     steps.push(...Stage.processEventSteps(stage, performer.scripts["help"]))
        // }

        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })
        steps.push(step)

        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return step

        Step.add(step, PlotFlourishes.NudgedMovement({
            "mark": hitmark,
            "unitKey": performer.key,
            "position": performer.position,
            "direction": direction,
        }))
        Step.add(step, {"mark": 0, "audio": {"triggerKey": "Unit:Heal"}})

        let healing = 20
        if(performer.properties?.includes("scaled-healing") || performer.equippedItem?.properties.includes("scaled-healing")) {
            healing = (performer.power || 0) + (performer.equippedItem?.power || 0)
        }
        // if(target.health + healing > target.maxhealth) {
        //     healing = target.maxhealth - target.health
        // }

        if(target.properties?.includes("can be pacified")) {
            Step.add(step, {
                "mark": hitmark,
                "unit": {
                    "key": target.key,
                    "state": "pacified",
                }
            })
        }

        Step.add(step, {
            "mark": hitmark,
            "unit": {
                "key": target.key,
                "health": Math.min(target.maxhealth, target.health + healing)
            },
        })

        if(performer.properties != undefined
        && performer.properties.includes("mutually-healing")) {
            Step.add(step, {
                "mark": hitmark,
                "unit": {
                    "key": performer.key,
                    "health": Math.min(performer.maxhealth, performer.health + healing)
                },
            })
        }

        if(target.effects.burning != undefined) {
            Step.add(step, {
                "mark": hitmark,
                "unit": {
                    "key": target.key,
                    "effects": {
                        "burning": undefined,
                    }
                },
            })
        }

        Step.add(step, PlotFlourishes.NudgedMovement({
            "mark": hitmark,
            "unitKey": target.key,
            "position": target.position,
            "direction": Geometry.flip(direction),
        }))
        Step.add(step, PlotFlourishes.TextFloat({
            "mark": hitmark,
            "type": "healing",
            "unitKey": target.key,
            "position": target.position,
            "text": "+" + (healing / 10) // DICE
        }))

        Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:hasHealed", true)})

        if(target.maxhealth != target.health
        && performer.scripts != undefined
        && performer.scripts["help"] != undefined) {
            steps.push(...Stage.processEventSteps(step.omegastage, performer.scripts["help"]))

            // ..
        }

        return steps
    }
}

skills["hero/bigheal"] = {
    ...skills["restore-health"],
    "range": {
        "min": 1,
        "max": 4,
        "orthogonal": true,
        "diagonal": true
    },
}


skills["armor-up"] = {
    "key": "armor-up",
    "name": "Armor Up",
    "role": "support",

    "type": "invoke",
    "range": null,

    "getPriority": getSupportPriority,
    "canTarget": function({performerKey, stage, position}) {
        const performer = stage.units[performerKey]
        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return false
        if(target.type == "thing") return false
        if(target.key == performer.key) return false
        if(target.affiliation != performer.affiliation
        && target.properties?.includes("can be pacified") == false) {
            return false
        }
        return true
    },

    "power": 30, // DICE

    "calculateStep": function({performerKey, effectKey, stage, position}) {
        const steps = []

        const performer = stage.units[performerKey]
        const direction = Geometry.direction(position, performer.position)

        const hitmark = 0

        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })
        steps.push(step)

        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return step

        Step.add(step, PlotFlourishes.NudgedMovement({
            "mark": hitmark,
            "unitKey": performer.key,
            "position": performer.position,
            "direction": direction,
        }))
        Step.add(step, {"mark": 0, "audio": {"triggerKey": "Unit:Heal"}})

        let armor = this.power

        Step.add(step, {
            "mark": hitmark,
            "unit": {
                "key": target.key,
                "armor": (target.armor || 0) + armor
            },
        })

        Step.add(step, {
            "mark": hitmark,
            "unit": {
                "key": performer.key,
                "armor": (performer.armor || 0) + armor
            },
        })

        Step.add(step, PlotFlourishes.NudgedMovement({
            "mark": hitmark,
            "unitKey": target.key,
            "position": target.position,
            "direction": Geometry.flip(direction),
        }))
        Step.add(step, PlotFlourishes.TextFloat({
            "mark": hitmark,
            "type": "healing",
            "unitKey": target.key,
            "position": target.position,
            "text": "+" + (armor / 10) // DICE
        }))

        return steps
    }
}

skills["pirates/interact"] = {
    "key": "pirates/interact",
    "name": "Interact",
    "role": "support",

    "type": "invoke",
    "range": 1,

    "isHidden": true,

    "getPriority": function({performerKey, stage, position}) {
        // const performer = stage.units[performerKey]
        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return 0
        if(target.affiliation == "none") return 6
        if(target.affiliation == "neutral") return 6
        if(target.affiliation == "nothing") return 6
        return 0
    },

    "canTarget": function({performerKey, stage, position}) {
        // const performer = stage.units[performerKey]
        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return false
        if(target.jobKey?.includes("chest") && target.health == target.maxhealth) return false
        if(target.affiliation == "none") return true
        if(target.affiliation == "neutral") return true
        if(target.affiliation == "nothing") return true
        return false
    },

    "calculateStep": function({stage, position, performerKey, effectKey}) {
        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })
        let steps = [step]

        const performer = step.omegastage.units[performerKey]
        const direction = Geometry.direction(position, performer.position)
        // const distance = Geometry.distance(position, performer.position)

        Step.add(step, PlotFlourishes.SwingBackMovement({
            "mark": 0,
            "unitKey": performer.key,
            "position": performer.position,
            "direction": direction,
        }))
        Step.add(step, {"mark": 180, "audio": {"triggerKey": performer.attackaudio ?? "Unit:Attack:Melee"}})

        const target = Stage.findUnitByPosition(step.omegastage, position)
        if(target == undefined) return

        if(target.type == "thing") {
            if(target.jobKey?.includes("chest")
            && target.health < target.maxhealth) {
                Step.add(step, {"mark": 200, ...Step.deltaflag("tutorial:hasDug", (stage.flags["tutorial:hasDug"] ?? 0) + 1)})
                Step.add(step, {
                    "mark": 200,
                    "unit": {
                        "key": target.key,
                        "health": target.health + 10,
                        "imageKey": "chest" + Math.floor((target.health + 10) / 10)
                    }
                })
                if(target.health >= target.maxhealth) {
                    stage.scripts.forEach((condition) => {
                        if(condition.trigger == "recruiting"
                        && condition.canBeTriggered(stage, performer, target) == true) {
                            const conditionEvents = condition.events
                            conditionEvents.unshift({
                                "omegastage": stage,
                                "name": "SavestateCapture",
                                "savestateSlug": condition.title || "Condition_" + stage.scripts.indexOf(condition),
                            })
                            steps = steps.concat(Stage.processEventSteps(step.omegastage, conditionEvents))
                        }
                    })
                }
            }
        }

        if(target.affiliation == "none"
        || target.affiliation == "neutral") {
            Step.add(step, {
                "mark": 200,
                "unit": {
                    "key": target.key,
                    "affiliation": performer.affiliation,
                    "decalImageKey": "",
                }
            })

            stage.scripts.forEach((condition) => {
                if(condition.trigger == "recruiting"
                && condition.canBeTriggered(stage, performer, target) == true) {
                    const conditionEvents = condition.events
                    // conditionEvents.unshift({
                    //     "omegastage": stage,
                    //     "name": "SavestateCapture",
                    //     "savestateSlug": condition.title || "Condition_" + stage.scripts.indexOf(condition),
                    // })
                    steps = steps.concat(Stage.processEventSteps(step.omegastage, conditionEvents))
                }
            })
        }

        return steps
    }
}

skills["dash"] = {
    "key": "dash",
    "name": "Dash",
    "role": "support",

    "type": "invoke",
    "range": 0,

    "getPriority": function() {
        return 1
    },
    "canTarget": function({performerKey, stage, position}) {
        const performer = stage.units[performerKey]
        return performer.energy["move"] == false
    },

    "calculateStep": function({performerKey, stage, position}) {
        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })

        const performer = stage.units[performerKey]
        // const direction = Geometry.direction(position, performer.position)

        Step.add(step, PlotFlourishes.SwingBackMovement({
            "mark": 0,
            "unitKey": performer.key,
            "position": performer.position,
            "direction": {"x": -1},
        }))

        Step.add(step, {"mark": 0, "audio": {"triggerKey": "Unit:Attack:Utility"}})
        Step.add(step, {"mark": 200, "audio": {"triggerKey": "Unit:Attack"}})

        Step.add(step, {
            "mark": 200,
            "unit": {
                "key": performer.key,
                "energy": {"move": true},
            }
        })

        return step
    }
}

skills["rally"] = {
    "key": "rally",
    "name": "Refresh",
    "role": "support",

    "description": "Shanty: Sing a song to an ally, refreshing their action and movement for the turn.",

    "type": "invoke",
    "range": 1,

    "getPriority": getSupportPriority,
    "canTarget": function({performerKey, stage, position}) {
        const performer = stage.units[performerKey]
        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return false
        if(target.affiliation != performer.affiliation) return false
        // if(target.energy["invoke skill"] == true && target.energy["move"] == true) return false
        return true
    },

    "calculateStep": function({performerKey, stage, position}) {
        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })
        const steps = [step]

        const performer = stage.units[performerKey]
        const direction = Geometry.direction(position, performer.position)

        Step.add(step, PlotFlourishes.SwingBackMovement({
            "mark": 0,
            "unitKey": performer.key,
            "position": performer.position,
            "direction": direction,
        }))

        Step.add(step, {"mark": 0, "audio": {"triggerKey": "Unit:Attack:Utility"}})
        Step.add(step, {"mark": 200, "audio": {"triggerKey": "Unit:Attack"}})

        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return steps

        Step.add(step, {
            "mark": 200,
            "unit": {
                "key": target.key,
                "energy": {"move": true, "invoke skill": true},
            }
        })
        Step.add(step, PlotFlourishes.NudgedMovement({
            "mark": 200,
            "unitKey": target.key,
            "position": target.position,
            "direction": direction,
        }))
        if(stage.flags["tutorial:hasRallied"] != true) {
            Step.add(step, {"mark": 200, ...Step.deltaflag("tutorial:hasRallied", true)})
        }
        if(performer.properties?.includes("dice/speedup") || performer.equippedItem?.properties?.includes("dice/speedup")) {
            Step.add(step, {
                "mark": 200,
                "unit": {
                    "key": target.key,
                    "moverange": 3,
                }
            })
        }
        // if(performer.properties?.includes("buff-rally")) {
        //     const power = performer.power ?? performer.equippedItem?.power
        //     Step.add(step, {
        //         "mark": 200,
        //         "unit": {
        //             "key": target.key,
        //             "power": (target.power || 0) + (power)
        //         }
        //     })
        // }

        stage.scripts.forEach((condition) => {
            if(condition.trigger == "supporting"
            && condition.canBeTriggered(stage, performer, target) == true) {
                steps.push(...Stage.processEventSteps(step.omegastage, condition.events))
            }
        })

        return steps
    }
}

skills["stun"] = {
    "key": "stun",
    "name": "Stun",
    "role": "damage", // TODO: make this be called, like, aggro
    "isRenderedEvenIfCanNotTarget": true,

    "type": "invoke",
    "range": 1,

    "canTarget": function({performerKey, stage, position}) {
        const performer = stage.units[performerKey]
        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return false
        if(target.affiliation == performer.affiliation) return false
        return true
    },

    "calculateStep": function({performerKey, stage, position}) {
        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })
        const steps = [step]

        const performer = stage.units[performerKey]
        const direction = Geometry.direction(position, performer.position)

        Step.add(step, PlotFlourishes.SwingBackMovement({
            "mark": 0,
            "unitKey": performer.key,
            "position": performer.position,
            "direction": direction,
        }))

        Step.add(step, {"mark": 0, "audio": {"triggerKey": "Unit:Attack:Utility"}})
        Step.add(step, {"mark": 200, "audio": {"triggerKey": "Unit:Attack"}})

        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return steps

        Step.add(step, {
            "mark": 200,
            "unit": {
                "key": target.key,
                "energy": {"move": false, "invoke skill": false},
            }
        })
        Step.add(step, PlotFlourishes.Shudder({
            "mark": 200,
            "unitKey": target.key,
            "position": target.position,
            "intensity": 0.1
        }))

        return steps
    }
}

skills["strengthen"] = {
    "key": "strengthen",
    "name": "Strengthen",
    "role": "support",

    "type": "invoke",
    "range": 1,

    "getPriority": getSupportPriority,
    "canTarget": function({performerKey, stage, position}) {
        const performer = stage.units[performerKey]
        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return false
        if(target.affiliation != performer.affiliation) return false
        return true
    },

    "calculateStep": function({performerKey, stage, position}) {
        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })
        const steps = [step]

        const performer = stage.units[performerKey]
        const direction = Geometry.direction(position, performer.position)

        Step.add(step, PlotFlourishes.SwingBackMovement({
            "mark": 0,
            "unitKey": performer.key,
            "position": performer.position,
            "direction": direction,
        }))

        Step.add(step, {"mark": 0, "audio": {"triggerKey": "Unit:Attack:Utility"}})
        Step.add(step, {"mark": 200, "audio": {"triggerKey": "Unit:Attack"}})

        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return steps

        Step.add(step, PlotFlourishes.NudgedMovement({
            "mark": 200,
            "unitKey": target.key,
            "position": target.position,
            "direction": direction,
        }))
        const power = performer.power || performer.equippedItem?.power
        Step.add(step, {
            "mark": 200,
            "unit": {
                "key": target.key,
                "power": (target.power || 0) + (power)
            }
        })

        stage.scripts.forEach((condition) => {
            if(condition.trigger == "supporting"
            && condition.canBeTriggered(stage, performer, target) == true) {
                steps.push(...Stage.processEventSteps(step.omegastage, condition.events))
            }
        })

        return steps
    }
}

skills["weaken"] = {
    "key": "weaken",
    "name": "Weaken",
    "role": "attack",

    "type": "invoke",
    "range": 1,

    "getPriority": getSupportPriority,
    "canTarget": function({performerKey, stage, position}) {
        const performer = stage.units[performerKey]
        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return false
        if(target.affiliation == performer.affiliation) return false
        return true
    },

    "calculateStep": function({performerKey, stage, position}) {
        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })
        const steps = [step]

        const performer = stage.units[performerKey]
        const direction = Geometry.direction(position, performer.position)

        Step.add(step, PlotFlourishes.SwingBackMovement({
            "mark": 0,
            "unitKey": performer.key,
            "position": performer.position,
            "direction": direction,
        }))

        Step.add(step, {"mark": 0, "audio": {"triggerKey": "Unit:Attack:Utility"}})
        Step.add(step, {"mark": 200, "audio": {"triggerKey": "Unit:Attack"}})

        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return steps

        Step.add(step, PlotFlourishes.NudgedMovement({
            "mark": 200,
            "unitKey": target.key,
            "position": target.position,
            "direction": direction,
        }))
        const power = performer.power || performer.equippedItem?.power
        Step.add(step, {
            "mark": 200,
            "unit": {
                "key": target.key,
                "power": (target.power || 0) - (power)
            }
        })

        stage.scripts.forEach((condition) => {
            if(condition.trigger == "supporting"
            && condition.canBeTriggered(stage, performer, target) == true) {
                steps.push(...Stage.processEventSteps(step.omegastage, condition.events))
            }
        })

        return steps
    }
}

skills["speedup"] = {
    "key": "speedup",
    "name": "Speedup",
    "role": "support",

    "type": "invoke",
    "range": 1,

    "getPriority": getSupportPriority,
    "canTarget": function({performerKey, stage, position}) {
        const performer = stage.units[performerKey]
        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return false
        if(target.affiliation != performer.affiliation) return false
        return true
    },

    "calculateStep": function({performerKey, stage, position}) {
        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })
        const steps = [step]

        const performer = stage.units[performerKey]
        const direction = Geometry.direction(position, performer.position)

        Step.add(step, PlotFlourishes.SwingBackMovement({
            "mark": 0,
            "unitKey": performer.key,
            "position": performer.position,
            "direction": direction,
        }))

        Step.add(step, {"mark": 0, "audio": {"triggerKey": "Unit:Attack:Utility"}})
        Step.add(step, {"mark": 200, "audio": {"triggerKey": "Unit:Attack"}})

        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return steps

        Step.add(step, PlotFlourishes.NudgedMovement({
            "mark": 200,
            "unitKey": target.key,
            "position": target.position,
            "direction": direction,
        }))
        const power = performer.power ?? performer.equippedItem?.power
        Step.add(step, {
            "mark": 200,
            "unit": {
                "key": target.key,
                "moverange": (target.moverange ?? 0) + (power)
            }
        })

        stage.scripts.forEach((condition) => {
            if(condition.trigger == "supporting"
            && condition.canBeTriggered(stage, performer, target) == true) {
                steps.push(...Stage.processEventSteps(step.omegastage, condition.events))
            }
        })

        return steps
    }
}

skills["book/ritual"] = {
    "key": "book/ritual",
    "name": "Ritual Circle",
    "role": "support",

    "description": "Drop a ritual to contain a monster!",

    "type": "invoke",
    "range": 1,

    "getPriority": function getSupportPriority({performerKey, stage, position}) {
        return 0
    },
    "isHidden": function isHidden({performerKey, stage}) {
        return !!Object.values(stage.units).find((unit) => unit.role === "ritual")
    },
    "canTarget": function({performerKey, stage, position}) {
        return !Object.values(stage.units).find((unit) => unit.role === "ritual")
    },

    "calculateStep": function({performerKey, stage, position}) {
        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })
        const steps = [step]

        const performer = stage.units[performerKey]
        const direction = Geometry.direction(position, performer.position)

        Step.add(step, PlotFlourishes.SwingBackMovement({
            "mark": 0,
            "unitKey": performer.key,
            "position": performer.position,
            "direction": direction,
        }))

        Step.add(step, {"mark": 0, "audio": {"triggerKey": "Unit:Attack:Utility"}})
        Step.add(step, {"mark": 200, "audio": {"triggerKey": "Unit:Attack"}})

        Step.add(step, {
            "mark": 200,
            "unit": {
                "role": "ritual",
                "type": "zone",
                "maxhealth": 10,
                "sprites": {
                    "idle": require("@frog/source/assets/images/book-tactics/ludum-sigil.png"),
                },
                "spriteSize": "3x3",
                "position": {
                    "x": position.x,
                    "y": position.y,
                    "z": position.z,
                },
                "truestack": 1,
                "affiliation": "none",
                "properties": ["no healthbar", "beneath everything else"],
            }
        })
        Step.add(step, {
            "mark": 200,
            "player": {
                "phase": "ritual",
            }
        })
        steps.push(Step.instantiate({
            "alphastage": step.omegastage,
            "func": () => {
                const timeout = Math.min(stage.player.timeout, App?.game.chapter.timer + (1 * 60 * 1000) - 100)
                if(!!App?.game.chapter.stage.player.timeout) {
                    App.game.chapter.stage.player.timeout = timeout
                }
            }
        }))

        return steps
    }
}

skills["book/brandish"] = {
    "key": "book/brandish",
    "name": "Brandish",
    "role": "equip",

    "type": "invoke",
    "range": 0,

    "getPriority": () => -1,
    "canTarget": function({performerKey, stage, position}) {
        const performer = stage.units[performerKey]
        return performer.equippedItem == undefined
            || performer.equippedItem.skillKeys?.includes("book/unbrandish") == false
    },
    "isHidden": function isHidden({performerKey, stage}) {
        const performer = stage.units[performerKey]
        return performer.equippedItem != undefined
            && performer.equippedItem.skillKeys?.includes("book/unbrandish") != false
    },

    "calculateStep": function({performerKey, stage, position}) {
        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })

        const performer = stage.units[performerKey]
        const direction = Geometry.direction(position, performer.position)

        Step.add(step, PlotFlourishes.JumpMovement({
            "mark": 0,
            "unitKey": performer.key,
            "startPosition": performer.position,
            "endPosition": performer.position,
        }))

        Step.add(step, {
            "mark": 200,
            "unit": {
                "key": performer.key,
                "equippedItem": performer.primaryItem, // TODO: figure out which item is being called for this
            }
        })

        Object.values(stage.units).forEach((unit) => {
            if(unit.stimuli != undefined
            && unit.stimuli["brandish:" + performer.equippedItem?.key]) {
                Step.add(step, {
                    "mark": 200,
                    "unit": {
                        "key": unit.key,
                        // "role": "visible",
                        "state": unit.stimuli["brandish:" + performer.equippedItem?.key]
                    }
                })
            }
        })

        return step
    }
}
skills["book/unbrandish"] = {
    "key": "book/unbrandish",
    "name": "Unbrandish",
    "role": "equip",

    "type": "invoke",
    "range": 0,

    "getPriority": () => -1,
    "canTarget": function({performerKey, stage, position}) {
        const performer = stage.units[performerKey]
        return performer.equippedItem != undefined
            // && performer.equippedItem.skillKeys?.includes("book/unbrandish") != false
    },
    "isHidden": function isHidden({performerKey, stage}) {
        const performer = stage.units[performerKey]
        return performer.equippedItem == undefined
            || performer.equippedItem.skillKeys?.includes("book/unbrandish") == false
    },

    "calculateStep": function({performerKey, stage, position}) {
        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })

        const performer = stage.units[performerKey]
        const direction = Geometry.direction(position, performer.position)

        Step.add(step, PlotFlourishes.JumpMovement({
            "mark": 0,
            "unitKey": performer.key,
            "startPosition": performer.position,
            "endPosition": performer.position,
        }))
        Step.add(step, {
            "mark": 200,
            "unit": {
                "key": performer.key,
                "equippedItem": null
            }
        })

        Object.values(stage.units).forEach((unit) => {
            if(unit.stimuli != undefined
            && unit.stimuli["unbrandish:" + performer.equippedItem?.key]) {
                Step.add(step, {
                    "mark": 200,
                    "unit": {
                        "key": unit.key,
                        // "role": "visible",
                        "state": unit.stimuli["unbrandish:" + performer.equippedItem?.key]
                    }
                })
            }
        })

        return step
    }
}

skills["book/salt"] = {
    "key": "book/salt",
    "name": "Salt",
    "role": "support",

    "description": "Drop salt",

    "type": "invoke",
    "range": 1,

    "getPriority": function getSupportPriority({performerKey, stage, position}) {
        return 0
    },
    "canTarget": function({performerKey, stage, position}) {
        // const target = Stage.findUnitByPosition(stage, position, {includingZones: true})
        // return target?.role !== "scatterable:salt"
        return true
    },

    "calculateStep": function({performerKey, stage, position}) {
        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })
        const steps = [step]

        const performer = stage.units[performerKey]
        const direction = Geometry.direction(position, performer.position)

        Step.add(step, PlotFlourishes.SwingBackMovement({
            "mark": 0,
            "unitKey": performer.key,
            "position": performer.position,
            "direction": direction,
        }))

        Step.add(step, {"mark": 0, "audio": {"triggerKey": "Unit:Attack:Utility"}})
        Step.add(step, {"mark": 200, "audio": {"triggerKey": "Unit:Attack"}})

        Step.add(step, {
            "mark": 200,
            "unit": {
                "role": "scatterable:salt",
                "jobKey": "salt",
                "type": "zone",
                "maxhealth": 40,
                "spriteSize": "contained",
                "sprites": {
                    "idle": require("@frog/source/assets/images/book-tactics/ludum-scatterable-salt.png"),
                },
                "position": {
                    "x": position.x,
                    "y": position.y,
                    "z": position.z,
                },
                "stack": 0,
                "affiliation": "neutral",
            }
        })

        Object.values(stage.units).forEach((unit) => {
            if(unit.stimuli != undefined
            && unit.stimuli["scatter:salt"]) {
                Step.add(step, {
                    "mark": 200,
                    "unit": {
                        "key": unit.key,
                        // "role": "visible",
                        "state": unit.stimuli["scatter:salt"]
                    }
                })
            }
        })

        return steps
    }
}

skills["book/sulfur"] = {
    "key": "book/sulfur",
    "name": "Sulfur",
    "role": "support",

    "description": "Drop sulfur",

    "type": "invoke",
    "range": 1,

    "getPriority": function getSupportPriority({performerKey, stage, position}) {
        return 0
    },
    "canTarget": function({performerKey, stage, position}) {
        // const target = Stage.findUnitByPosition(stage, position, {includingZones: true})
        // return target?.role !== "scatterable:sulfur"
        return true
    },

    "calculateStep": function({performerKey, stage, position}) {
        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })
        const steps = [step]

        const performer = stage.units[performerKey]
        const direction = Geometry.direction(position, performer.position)

        Step.add(step, PlotFlourishes.SwingBackMovement({
            "mark": 0,
            "unitKey": performer.key,
            "position": performer.position,
            "direction": direction,
        }))

        Step.add(step, {"mark": 0, "audio": {"triggerKey": "Unit:Attack:Utility"}})
        Step.add(step, {"mark": 200, "audio": {"triggerKey": "Unit:Attack"}})

        Step.add(step, {
            "mark": 200,
            "unit": {
                "role": "scatterable:sulfur",
                "type": "zone",
                "jobKey": "sulfur",
                "maxhealth": 30,
                "spriteSize": "contained",
                "sprites": {
                    "idle": require("@frog/source/assets/images/book-tactics/ludum-scatterable-sulfur.png"),
                },
                "position": {
                    "x": position.x,
                    "y": position.y,
                    "z": position.z,
                },
                "stack": 0,
                "affiliation": "neutral",
            }
        })

        Object.values(stage.units).forEach((unit) => {
            if(unit.stimuli != undefined
            && unit.stimuli["scatter:sulfur"]) {
                Step.add(step, {
                    "mark": 200,
                    "unit": {
                        "key": unit.key,
                        // "role": "visible",
                        "state": unit.stimuli["scatter:sulfur"]
                    }
                })
            }
        })

        return steps
    }
}

skills["book/silver"] = {
    "key": "book/silver",
    "name": "Silver",
    "role": "support",

    "description": "Drop silver",

    "type": "invoke",
    "range": 1,

    "getPriority": function getSupportPriority({performerKey, stage, position}) {
        return 0
    },
    "canTarget": function({performerKey, stage, position}) {
        // const target = Stage.findUnitByPosition(stage, position, {includingZones: true})
        // return target?.role !== "scatterable:silver"
        return true
    },

    "calculateStep": function({performerKey, stage, position}) {
        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })
        const steps = [step]

        const performer = stage.units[performerKey]
        const direction = Geometry.direction(position, performer.position)

        Step.add(step, PlotFlourishes.SwingBackMovement({
            "mark": 0,
            "unitKey": performer.key,
            "position": performer.position,
            "direction": direction,
        }))

        Step.add(step, {"mark": 0, "audio": {"triggerKey": "Unit:Attack:Utility"}})
        Step.add(step, {"mark": 200, "audio": {"triggerKey": "Unit:Attack"}})

        Step.add(step, {
            "mark": 200,
            "unit": {
                "role": "scatterable:silver",
                "type": "zone",
                "jobKey": "silver",
                "maxhealth": 50,
                "spriteSize": "contained",
                "sprites": {
                    "idle": require("@frog/source/assets/images/book-tactics/ludum-scatterable-silver.png"),
                },
                "position": {
                    "x": position.x,
                    "y": position.y,
                    "z": position.z,
                },
                "stack": 0,
                "affiliation": "neutral",
            }
        })

        Object.values(stage.units).forEach((unit) => {
            if(unit.stimuli != undefined
            && unit.stimuli["scatter:silver"]) {
                Step.add(step, {
                    "mark": 200,
                    "unit": {
                        "key": unit.key,
                        // "role": "visible",
                        "state": unit.stimuli["scatter:silver"]
                    }
                })
            }
        })

        return steps
    }
}

skills["push"] = {
    "key": "push",
    "name": "Push",
    "role": "reposition",

    "description": "Push: Can push an ally 1 tile.",

    "type": "invoke",
    "range": 1,

    "canTarget": canTarget,
    "getPriority": getSupportPriority,

    "calculateStep": function({performerKey, stage, position}) {
        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })

        const performer = stage.units[performerKey]
        const direction = Geometry.direction(position, performer.position)
        const vector = Geometry.vector(position, performer.position)

        Step.add(step, PlotFlourishes.SwingBackMovement({
            "mark": 0,
            "unitKey": performer.key,
            "position": performer.position,
            "direction": direction,
        }))

        const previousPosition = position
        const nextPosition = Geometry.add(position, vector)

        Step.add(step, {
            "intent": {
                "type": "reposition",
                "positions": [previousPosition, nextPosition]
            },
        })

        Step.add(step, {"mark": 0, "audio": {"triggerKey": "Unit:Attack:Utility"}})
        Step.add(step, {"mark": 200, "audio": {"triggerKey": "Unit:Attack"}})

        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return step

        const collision = Stage.findCollision({
            stage, previousPosition, nextPosition,
        })

        if(collision == undefined) {
            Step.add(step, PlotFlourishes.PushedMovement({
                "mark": 200,
                "unitKey": target.key,
                "startPosition": previousPosition,
                "endPosition": nextPosition,
            }))
            // Step.add(step, {
            //     "mark": 200,
            //     "unit": {
            //         "key": target.key,
            //         "position": nextPosition,
            //     }
            // })
        } else if(collision != undefined) {
            Step.add(step, PlotFlourishes.NudgedMovement({
                "mark": 200,
                "unitKey": target.key,
                "position": target.position,
                "direction": direction,
            }))

            const damage = 10
            Step.add(step, {
                "mark": 200,
                "unit": {
                    "key": target.key,
                    "health": target.health - damage
                },
            })

            if(collision.unit != undefined) {
                Step.add(step, {
                    "mark": 200,
                    "unit": {
                        "key": collision.unit.key,
                        "health": collision.unit.health - damage
                    },
                })
            }
        }

        return step
    }
}

skills["swap"] = {
    "key": "swap",
    "name": "Swap",
    "role": "reposition",

    "description": "Swap: Can swap places with an ally.",

    "type": "invoke",
    "range": 1,

    "canTarget": canTarget,
    "getPriority": getSupportPriority,

    "calculateStep": function({performerKey, stage, position}) {
        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })

        const performer = stage.units[performerKey]
        const direction = Geometry.direction(position, performer.position)

        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) {
            Step.add(step, [
                {"mark": 0, "audio": {"triggerKey": "Unit:Attack:Utility"}}
            ])
            Step.add(step, PlotFlourishes.SwingBackMovement({
                "mark": 0,
                "unitKey": performer.key,
                "position": performer.position,
                "direction": direction,
            }))
            return step
        }

        Step.add(step, PlotFlourishes.PushedMovement({
            "mark": 0,
            "unitKey": performer.key,
            "startPosition": performer.position,
            "endPosition": target.position,
        }))
        Step.add(step, PlotFlourishes.PushedMovement({
            "mark": 0,
            "unitKey": target.key,
            "startPosition": target.position,
            "endPosition": performer.position,
        }))
        Step.add(step, [
            // {
            //     "mark": 0,
            //     "unit": {
            //         "key": performer.key,
            //         "position": target.position,
            //     }
            // },
            // {
            //     "mark": 0,
            //     "unit": {
            //         "key": target.key,
            //         "position": performer.position,
            //     }
            // },
            {"mark": 0, "audio": {"triggerKey": "Unit:Attack:Utility"}}
        ])

        return step
    }
}

skills["pull"] = {
    "key": "pull",
    "name": "Pullback",
    "role": "reposition",

    "description": "Pullback: Can pull an ally back a tile.",

    "type": "invoke",
    "range": 1,

    "canTarget": canTarget,
    "getPriority": getSupportPriority,

    "calculateStep": function({performerKey, stage, position}) {
        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })

        const performer = stage.units[performerKey]
        const direction = Geometry.direction(position, performer.position)
        const oppositeDirection = Geometry.flip(direction)

        if(direction.x != 0
        && direction.y != 0) {
            console.log("Cannot calculate pull skill with non-orthogonal direction.")
            return step
        }

        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) {
            return step
        }

        const previousPosition = performer.position
        const nextPosition = Geometry.add(performer.position, oppositeDirection)

        const previousTargetPosition = target.position
        const nextTargetPosition = Geometry.add(target.position, oppositeDirection)

        Step.add(step, {
            "intent": {
                "type": "reposition",
                "positions": [previousPosition, nextPosition]
            },
        })
        Step.add(step, {
            "intent": {
                "type": "reposition",
                "positions": [previousTargetPosition, nextTargetPosition]
            },
        })

        const collision = Stage.findCollision({
            "stage": stage,
            previousPosition,
            nextPosition,
        })

        if(collision == undefined) {
            // Step.add(step, {
            //     "mark": 0,
            //     "unit": {
            //         "key": performer.key,
            //         "position": nextPosition,
            //     },
            // })
            Step.add(step, PlotFlourishes.PushedMovement({
                "mark": 0,
                "unitKey": performer.key,
                "startPosition": previousPosition,
                "endPosition": nextPosition,
            }))
            // Step.add(step, {
            //     "mark": 0,
            //     "unit": {
            //         "key": target.key,
            //         "position": nextTargetPosition,
            //     }
            // })
            Step.add(step, PlotFlourishes.PushedMovement({
                "mark": 0,
                "unitKey": target.key,
                "startPosition": previousTargetPosition,
                "endPosition": nextTargetPosition,
            }))
            Step.add(step, {"mark": 0, "audio": {"triggerKey": "Unit:Pull"}})
        } else if(collision != undefined) {
            Step.add(step, PlotFlourishes.NudgedMovement({
                "mark": 0,
                "unitKey": performer.key,
                "position": performer.position,
                "direction": direction,
            }))
            Step.add(step, PlotFlourishes.NudgedMovement({
                "mark": 0,
                "unitKey": target.key,
                "position": target.position,
                "direction": direction,
            }))
            Step.add(step, {"mark": 0, "audio": {"triggerKey": "Unit:Attack:Utility"}})
        }

        return step
    }
}

skills["suplex"] = {
    "key": "suplex",
    "name": "Suplex",
    "role": "reposition",

    "description": "Suplex: Can flip an ally to your other side.",

    "type": "invoke",
    "range": 1,

    "canTarget": canTarget,
    "getPriority": getSupportPriority,

    "calculateStep": function({performerKey, stage, position}) {
        const step = Step.instantiate({"type": "invoke skill", "alphastage": stage})

        const performer = stage.units[performerKey]
        const direction = Geometry.direction(position, performer.position)

        Step.add(step, PlotFlourishes.SwingBackMovement({
            "mark": 0,
            "unitKey": performer.key,
            "position": performer.position,
            "direction": direction,
        }))
        Step.add(step, {"mark": 0, "audio": {"triggerKey": "Unit:Pull"}})

        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return step

        const previousPosition = performer.position
        const nextPosition = Geometry.add(performer.position, Geometry.flip(direction))

        const collision = Stage.findCollision({
            stage, previousPosition, nextPosition,
        })

        if(collision == undefined) {
            Step.add(step, {
                "mark": 200,
                "unit": {
                    "key": target.key,
                    "position": nextPosition,
                },
                "intent": {
                    "type": "reposition",
                    "positions": [target.position, performer.position, nextPosition]
                },
            })
            Step.add(step, PlotFlourishes.PushedMovement({
                "mark": 200,
                "unitKey": target.key,
                "startPosition": target.position,
                "endPosition": nextPosition,
            }))
        } else {
            Step.add(step, PlotFlourishes.NudgedMovement({
                "mark": 200,
                "unitKey": target.key,
                "position": target.position,
                "direction": direction,
            }))
        }

        return step
    }
}

skills["pivot"] = {
    "key": "pivot",
    "name": "Hop",
    "role": "reposition",

    "description": "Hop: Can hop over an ally.",

    "type": "invoke",
    "range": 1,
    "canTarget": canTarget,
    "getPriority": getSupportPriority,

    "calculateStep": function({performerKey, stage, position}) {
        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })

        const performer = stage.units[performerKey]
        const direction = Geometry.direction(position, performer.position)

        Step.add(step, PlotFlourishes.SwingBackMovement({
            "mark": 0,
            "unitKey": performer.key,
            "position": performer.position,
            "direction": direction,
        }))

        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return step

        const previousPosition = performer.position
        const nextPosition = Geometry.add(target.position, direction)

        const collision = Stage.findCollision({
            stage, previousPosition, nextPosition,
        })

        if(collision == undefined) {
            // Step.add(step, {
            //     "mark": 200,
            //     "unit": {
            //         "key": performer.key,
            //         "position": nextPosition,
            //     },
            //     "intent": {
            //         "type": "reposition",
            //         "positions": [performer.position, target.position, nextPosition]
            //     },
            // })
            Step.add(step, PlotFlourishes.PushedMovement({
                "mark": 200,
                "unitKey": performer.key,
                "startPosition": performer.position,
                "endPosition": nextPosition,
            }))
        } else {
            Step.add(step, PlotFlourishes.NudgedMovement({
                "mark": 200,
                "unitKey": performer.key,
                "position": performer.position,
                "direction": direction,
            }))
        }

        return step
    }
}

skills["smite"] = {
    "key": "smite",
    "name": "Push",
    "role": "reposition",

    "description": "Smite: Push an ally two tiles.",

    "type": "invoke",
    "range": 1,

    "canTarget": canTarget,
    "getPriority": getSupportPriority,

    "calculateStep": function({performerKey, stage, position}) {
        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })

        const performer = stage.units[performerKey]
        const direction = Geometry.direction(position, performer.position)

        Step.add(step, PlotFlourishes.SwingBackMovement({
            "mark": 0,
            "unitKey": performer.key,
            "position": performer.position,
            "direction": direction,
        }))

        Step.add(step, {"mark": 0, "audio": {"triggerKey": "Unit:Attack:Utility"}})
        Step.add(step, {"mark": 200, "audio": {"triggerKey": "Unit:Attack"}})

        const intendedDistance = 2
        const ray = Stage.findCollisionInDirection({
            "stage": stage,
            "position": position,
            "direction": direction,
            "maxdistance": intendedDistance,
        })

        Step.add(step, {
            "mark": 0,
            "intent": {
                "type": "reposition",
                "positions": ray.castedPositions
            }
        })

        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return step

        if(ray.distance > 0) {
            Step.add(step, PlotFlourishes.PushedMovement({
                "mark": 200,
                "unitKey": target.key,
                "startPosition": ray.startPosition,
                "endPosition": ray.endPosition,
            }))
        } else {
            Step.add(step, PlotFlourishes.NudgedMovement({
                "mark": 200,
                "unitKey": target.key,
                "position": target.position,
                "direction": direction,
            }))
        }

        // if(ray.distance < intendedDistance) {
        //     const damage = intendedDistance - ray.distance
        //     Step.add(step, {
        //         "mark": 200,
        //         "unit": {
        //             "key": target.key,
        //             "health": target.health - damage
        //         },
        //     })
        //
        //     if(ray?.collision?.unit != undefined) {
        //         Step.add(step, {
        //             "mark": 200,
        //             "unit": {
        //                 "key": ray.collision.unit.key,
        //                 "health": ray.collision.unit.health - damage
        //             },
        //         })
        //     }
        // }

        return step
    }
}

skills["ember/harvest"] = {
    "key": "ember/harvest",
    "name": "Harvest",
    "role": "harvest",

    "type": "invoke",
    "range": 1,

    "isHidden": true,

    "getPriority": function({performerKey, stage, position}) {
        // const performer = stage.units[performerKey]
        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return 0
        return 6
    },

    "canTarget": function({performerKey, stage, position}) {
        // const performer = stage.units[performerKey]
        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return false
        return target.type == "thing"
            && target.role == "plant"
        return false
    },

    "calculateStep": function({stage, position, performerKey, effectKey}) {
        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })

        const performer = step.omegastage.units[performerKey]
        const direction = Geometry.direction(position, performer.position)
        // const distance = Geometry.distance(position, performer.position)

        Step.add(step, {"mark": 180, "audio": {"triggerKey": "Unit:Loot"}})
        Step.add(step, PlotFlourishes.SwingBackMovement({
            "mark": 0,
            "unitKey": performer.key,
            "position": performer.position,
            "direction": direction,
        }))

        const target = Stage.findUnitByPosition(step.omegastage, position)
        if(target == undefined) return step

        if(target.type == "thing"
        && target.role == "plant") {
            Step.add(step, {"mark": 200, ...Step.deltaflag("tutorial:hasLooted", true)})
            Step.add(step, {
                "mark": 200,
                "unit": {
                    "key": target.key,
                    "health": target.health - 10,
                }
            })

            const item = Object.values(target.inventory?.items || [])[0]
            if(item != undefined) {
                Step.add(step, {
                    "mark": 200,
                    "unit": {
                        "key": performer.key,
                        "inventory": {
                            "items": {
                                [item.key]: item
                            }
                        }
                    }
                })

                Step.add(step, {
                    "mark": 200,
                    "unit": {
                        "key": target.key,
                        "inventory": {
                            "items": {
                                [item.key]: undefined
                            }
                        }
                    }
                })

                Step.add(step, PlotFlourishes.ImageFloat({
                    "mark": 200,
                    "position": performer.position,
                    "image": item.image
                }))
            }
        }

        return step
    }
}

skills["ember/kindle"] = {
    "key": "ember/kindle",
    "name": "Kindle",
    "role": "kindle",

    "type": "invoke",
    "range": 1,

    "isHidden": true,

    "getPriority": function({performerKey, stage, position}) {
        // const performer = stage.units[performerKey]
        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return 0
        return 6
    },

    "canTarget": function({performerKey, stage, position}) {
        // const performer = stage.units[performerKey]
        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return false
        return target.type == "thing"
            && target.role == "torch"
    },

    "calculateStep": function({stage, position, performerKey, effectKey}) {
        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })

        const performer = step.omegastage.units[performerKey]
        const direction = Geometry.direction(position, performer.position)
        // const distance = Geometry.distance(position, performer.position)

        Step.add(step, {"mark": 180, "audio": {"triggerKey": "Unit:Loot"}})
        Step.add(step, PlotFlourishes.SwingBackMovement({
            "mark": 0,
            "unitKey": performer.key,
            "position": performer.position,
            "direction": direction,
        }))

        const target = Stage.findUnitByPosition(step.omegastage, position)
        if(target == undefined) return step

        if(target.type == "thing"
        && target.role == "torch") {
            for(let i = 1; i <= target.data?.flameCount; i += 1) {
                Step.add(step, {
                    "mark": 200 + (50 * (i - 1)),
                    "unit": {
                        ...emberUnitJobs["ember/flame"],

                        "maxhealth": 10 + (i * 10),
                        "sprites": {
                            "idle": target.sprites["flame" + i],
                        },
                        "position": {
                            "x": target.position.x,
                            "y": target.position.y,
                            "z": target.position.z,
                        },
                        "stack": 10 + (i - target.data.flameCount),
                    }
                })
            }
        }

        return step
    }
}

skills["heal-every-turn"] = {
    "key": "ember/burnout",
    "type": "at-start-of-my-turn",
    "isHidden": true,
    "range": 0,

    "calculateStep": function({stage, position, performerKey, effectKey}) {
        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })

        const performer = step.omegastage.units[performerKey]
        if(performer.health < performer.maxhealth) {
            // Step.add(step, PlotFlourishes.Shudder({
            //     "unitKey": performer.key,
            //     "position": performer.position,
            //     "intensity": 0.05,
            // }))
            Step.add(step, {
                "mark": 0,
                "unit": {
                    "key": performerKey,
                    "health": Math.min(performer.health + 10, performer.maxhealth),
                }
            })
            Step.add(step, PlotFlourishes.TextFloat({
                "mark": 10,
                "type": "healing",
                "unitKey": performer.key,
                "position": performer.position,
                "text": "+10hp",
            }))
        }

        return step
    }
}

skills["ember/burnout"] = {
    "key": "ember/burnout",
    "type": "at-end-of-player-turn",
    "isHidden": true,
    "range": 0,

    "calculateStep": function({stage, position, performerKey, effectKey}) {
        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })

        const performer = step.omegastage.units[performerKey]
        Step.add(step, {
            "mark": 0,
            "unit": {
                "key": performerKey,
                "health": performer.health - 10,
            }
        })

        if(performer.health == 0) {
            Step.add(step, PlotFlourishes.Shudder({
                "unitKey": performer.key,
                "position": performer.position,
                "intensity": 0.05,
            }))
        }

        return step
    }
}

skills["ember/recruit"] = {
    "key": "recruit",
    "name": "Recruit",
    "role": "support",

    "type": "invoke",
    "range": 1,

    "isHidden": true,

    "getPriority": function({performerKey, stage, position}) {
        // const performer = stage.units[performerKey]
        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return 0
        if(target.affiliation == "none") return 6
        if(target.affiliation == "neutral") return 6
        if(target.affiliation == "nothing") return 6
        return 0
    },

    "canTarget": function({performerKey, stage, position}) {
        // const performer = stage.units[performerKey]
        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return false
        if(target.affiliation == "none") return true
        if(target.affiliation == "neutral") return true
        if(target.affiliation == "nothing") return true
        return false
    },

    "calculateStep": function({stage, position, performerKey, effectKey}) {
        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })

        let steps = []
        steps.push(step)

        const performer = step.omegastage.units[performerKey]
        const direction = Geometry.direction(position, performer.position)
        // const distance = Geometry.distance(position, performer.position)

        Step.add(step, PlotFlourishes.SwingBackMovement({
            "mark": 0,
            "unitKey": performer.key,
            "position": performer.position,
            "direction": direction,
        }))

        Step.add(step, {"mark": 180, "audio": {"triggerKey": performer.attackaudio ?? "Unit:Attack:Melee"}})

        const target = Stage.findUnitByPosition(step.omegastage, position)
        if(target == undefined) return step

        if(target.affiliation == "none"
        || target.affiliation == "neutral") {
            Step.add(step, {
                "mark": 200,
                "unit": {
                    "key": target.key,
                    "affiliation": performer.affiliation,
                }
            })

            stage.scripts.forEach((condition) => {
                if(condition.trigger == "recruiting"
                && condition.canBeTriggered(stage, performer, target) == true) {
                    const conditionEvents = condition.events
                    conditionEvents.unshift({
                        "omegastage": stage,
                        "name": "SavestateCapture",
                        "savestateSlug": condition.title || "Condition_" + stage.scripts.indexOf(condition),
                    })
                    steps = steps.concat(Stage.processEventSteps(step.omegastage, conditionEvents))
                }
            })
        }

        return steps
    }
}

skills["dice/trade"] = {
    "key": "dice/trade",
    "name": "Trade",
    "role": "support",

    "type": "invoke",
    "range": 1,

    "canTarget": function({performerKey, stage, position}) {
        const performer = stage.units[performerKey]
        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return false
        if(target.affiliation == undefined) return false
        if(target.affiliation == "none") return false
        if(target.affiliation == "nothing") return false
        return true
    },
    "getPriority": getSupportPriority,

    "calculateStep": function({stage, position, performerKey, effectKey}) {
        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })

        let steps = []
        steps.push(step)

        const performer = step.omegastage.units[performerKey]
        const direction = Geometry.direction(position, performer.position)
        // const distance = Geometry.distance(position, performer.position)

        Step.add(step, PlotFlourishes.SwingBackMovement({
            "mark": 0,
            "unitKey": performer.key,
            "position": performer.position,
            "direction": direction,
        }))

        Step.add(step, {"mark": 180, "audio": {"triggerKey": performer.attackaudio ?? "Unit:Attack:Melee"}})

        const target = Stage.findUnitByPosition(step.omegastage, position)
        if(target == undefined) return step

        const targetNumber = target.number
        const performerNumber = performer.number
        // Step.add(step, {
        //     "mark": 200,
        //     "unit": {
        //         "key": target.key,
        //         "number": performerNumber,
        //         "power": performerNumber * 10,
        //         // "defense": performerNumber * 10,
        //     }
        // })
        Step.add(step, {
            "mark": 200,
            "unit": {
                "key": performer.key,
                "number": targetNumber,
                "power": targetNumber * 10,
                // "defense": targetNumber * 10,
            }
        })
        return steps
    }
}

skills["dice/double"] = {
    "key": "dice/double",
    "name": "Double",
    "role": "support",

    "type": "invoke",
    "range": 1,

    "canTarget": canTarget,
    "getPriority": getSupportPriority,

    "calculateStep": function({stage, position, performerKey, effectKey}) {
        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })

        let steps = []
        steps.push(step)

        const performer = step.omegastage.units[performerKey]
        const direction = Geometry.direction(position, performer.position)
        // const distance = Geometry.distance(position, performer.position)

        Step.add(step, PlotFlourishes.SwingBackMovement({
            "mark": 0,
            "unitKey": performer.key,
            "position": performer.position,
            "direction": direction,
        }))

        Step.add(step, {"mark": 180, "audio": {"triggerKey": performer.attackaudio ?? "Unit:Attack:Melee"}})

        const target = Stage.findUnitByPosition(step.omegastage, position)
        if(target == undefined) return step

        const targetNumber = target.number
        const performerNumber = performer.number
        Step.add(step, {
            "mark": 200,
            "unit": {
                "key": target.key,
                "number": performerNumber,
            }
        })
        Step.add(step, {
            "mark": 200,
            "unit": {
                "key": performer.key,
                "number": targetNumber,
            }
        })

        return steps
    }
}

// TODO: deprecate this someday :<
skills.get = function(skillKey) {
    return skills[skillKey]
}

skills["attack"] = skills["inflict-damage"]
skills["defend"] = skills["armor-up"]
skills["heal"] = skills["restore-health"]

export default skills
